import { cloneElement, useEffect } from 'react';

import type { IMaybeAsync } from '~/helpers/types';
import type { IAuthNavigateToLogInOptions } from './AuthClient';
import type { IAuthContextValue } from './AuthContext';
import { getCurrentLocation } from '~/components/utils/Navigation';
import { AuthContext } from './AuthContext';
import { useAuth } from './hooks/useAuth';

/**
 * Options for the withAuthenticationRequired Higher Order Component.
 */
export type IWithAuthenticationRequiredOptions = {
  /**
   * Allows executing logic before the user is redirected to the log-in page.
   */
  onBeforeAuthentication?: () => IMaybeAsync<void>;

  /**
   * Render a message to show that the user is being redirected to the log-in.
   */
  onRedirecting?: () => JSX.Element;

  /**
   * Pass additional log-in options.
   */
  logInOptions?: IAuthNavigateToLogInOptions;

  /**
   * The context to be used when calling useAuth, this should only be provided
   * if you are using multiple AuthProviders within your application and you
   * wish to tie a specific component to a AuthProvider other than the
   * AuthProvider associated with the default AuthContext.
   */
  context?: React.Context<IAuthContextValue>;
};

/**
 * When you wrap your components in this Higher Order Component and an anonymous
 * user visits your component, they will be redirected to the log-in page; after
 * log-in they will be returned to the page they were redirected from.
 */
export const withAuthenticationRequired = <TProps extends object>(
  Component: React.ReactElement<TProps>,
  options: IWithAuthenticationRequiredOptions = {},
): React.FC<TProps> =>
  function WithAuthenticationRequired(props: TProps): JSX.Element | undefined {
    const {
      context = AuthContext,
      onBeforeAuthentication,
      onRedirecting,
    } = options;
    const { isAuthenticated, isLogingIn, isLogingOut, navigateToLogIn } =
      useAuth(context);

    useEffect(() => {
      if (!isLogingOut && (isLogingIn || isAuthenticated)) {
        return;
      }

      const currentUrl = new URL(getCurrentLocation());
      const logInOptions = {
        ...options.logInOptions,
        nextUrl:
          options.logInOptions?.nextUrl ??
          (currentUrl.pathname === '/'
            ? undefined
            : `${currentUrl.pathname}${currentUrl.search}`),
      };

      void (async (): Promise<void> => {
        await onBeforeAuthentication?.();
        await navigateToLogIn(logInOptions);
      })();
    }, [
      isLogingOut,
      isLogingIn,
      isAuthenticated,
      navigateToLogIn,
      onBeforeAuthentication,
    ]);

    return isAuthenticated ? cloneElement(Component, props) : onRedirecting?.();
  };
